import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { tenantApi, memberApi, integrationApi, aiApi, type Tenant } from '../api';
import Icon from '../components/common/Icon';
import Select from '../components/common/Select';
import { humaniseRole } from '../lib/labels';

type StepKey = 'chambers_details' | 'branding' | 'team' | 'integrations' | 'first_matter' | 'complete';

const STEPS: { key: StepKey; title: string; description: string }[] = [
  { key: 'chambers_details', title: 'Chambers',      description: 'Name, address, VAT, timezone.' },
  { key: 'branding',         title: 'Branding',      description: 'Invoice letterhead details.' },
  { key: 'team',             title: 'Team',          description: 'Invite co-advocates, pupils or clerks.' },
  { key: 'integrations',     title: 'Integrations',  description: 'SMTP email + AI provider.' },
  { key: 'first_matter',     title: 'First matter',  description: 'Create something to try the app — or skip.' },
];

export default function Onboarding() {
  const nav = useNavigate();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    tenantApi.get()
      .then((r) => {
        setTenant(r.tenant);
        const idx = STEPS.findIndex((s) => s.key === r.tenant.onboarding_step);
        if (idx >= 0) setStepIdx(idx);
      })
      .catch((err) => {
        // Without this the page sits on "Loading…" forever when /tenant 403s
        // (e.g. tenant_id header mismatch). Surface the failure so the user
        // can recover instead of being stranded.
        setLoadError((err as Error).message || 'Could not load your chambers.');
      });
  }, []);

  const markComplete = async () => {
    await tenantApi.onboarding('complete');
    nav('/');
  };

  const advance = async (nextKey: StepKey) => {
    await tenantApi.onboarding(nextKey);
    if (nextKey === 'complete') { nav('/'); return; }
    const next = STEPS.findIndex((s) => s.key === nextKey);
    setStepIdx(Math.max(0, next));
    // Refresh tenant in case earlier step changed values
    tenantApi.get().then((r) => setTenant(r.tenant)).catch(() => null);
  };

  if (loadError) {
    return (
      <div className="card stack" style={{ maxWidth: 540, margin: '60px auto' }}>
        <h2>We couldn't start setup</h2>
        <p className="muted">{loadError}</p>
        <div className="row" style={{ gap: 8 }}>
          <button onClick={() => window.location.reload()}>Retry</button>
          <button className="primary" onClick={() => nav('/login')}>Sign in again</button>
        </div>
      </div>
    );
  }
  if (!tenant) return <div className="muted">Loading…</div>;

  const current = STEPS[stepIdx];

  return (
    <div style={{ maxWidth: 760, margin: '0 auto' }}>
      <header style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 28 }}>Set up {tenant.name}</h1>
        <p className="muted">A few quick steps to get your chambers ready.</p>
      </header>

      <div style={progressStyle}>
        {STEPS.map((s, i) => (
          <div key={s.key} style={{
            flex: 1,
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
            opacity: i > stepIdx ? 0.4 : 1,
          }}>
            <div style={{
              width: 28, height: 28, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 600,
              background: i <= stepIdx ? 'var(--color-accent)' : 'var(--color-surface-2)',
              color: i <= stepIdx ? '#1A1305' : 'var(--color-text-muted)',
              border: '1px solid ' + (i <= stepIdx ? 'var(--color-accent)' : 'var(--color-border)'),
            }}>{i < stepIdx ? '✓' : i + 1}</div>
            <div style={{ fontSize: 11, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{s.title}</div>
          </div>
        ))}
      </div>

      <div className="card stack" style={{ marginTop: 20 }}>
        <div>
          <h2>{current.title}</h2>
          <p className="muted" style={{ marginTop: 4 }}>{current.description}</p>
        </div>

        {current.key === 'chambers_details' && <ChambersStep tenant={tenant} onNext={() => advance('branding')} />}
        {current.key === 'branding'         && <BrandingStep tenant={tenant} onNext={() => advance('team')} onBack={() => setStepIdx(0)} />}
        {current.key === 'team'             && <TeamStep onNext={() => advance('integrations')} onBack={() => setStepIdx(1)} />}
        {current.key === 'integrations'     && <IntegrationsStep onNext={() => advance('first_matter')} onBack={() => setStepIdx(2)} />}
        {current.key === 'first_matter'     && <FirstMatterStep onNext={markComplete} onBack={() => setStepIdx(3)} />}
      </div>
    </div>
  );
}

const progressStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 12, padding: 18,
};

// ----------------- Steps -----------------

function ChambersStep({ tenant, onNext }: { tenant: Tenant; onNext: () => void }) {
  const [form, setForm] = useState({
    name: tenant.name,
    legal_name: tenant.legal_name || '',
    vat_number: tenant.vat_number || '',
    tax_number: tenant.tax_number || '',
    default_timezone: tenant.default_timezone,
    default_currency: tenant.default_currency,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true); setError(null);
    try {
      await tenantApi.update(form);
      onNext();
    } catch (err) { setError((err as Error).message); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} className="stack">
      <div>
        <label>Chambers / practice name</label>
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
      </div>
      <div>
        <label>Legal name (if different)</label>
        <input value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} />
      </div>
      <div className="row">
        <div className="grow">
          <label>VAT number (if registered)</label>
          <input value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} />
        </div>
        <div className="grow">
          <label>Tax number</label>
          <input value={form.tax_number} onChange={(e) => setForm({ ...form, tax_number: e.target.value })} />
        </div>
      </div>
      <div className="row">
        <div className="grow">
          <label>Timezone</label>
          <input value={form.default_timezone} onChange={(e) => setForm({ ...form, default_timezone: e.target.value })} />
        </div>
        <div className="grow">
          <label>Default currency</label>
          <input value={form.default_currency} onChange={(e) => setForm({ ...form, default_currency: e.target.value })} />
        </div>
      </div>
      {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}
      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <button type="submit" className="primary" disabled={saving}>{saving ? 'Saving…' : 'Continue →'}</button>
      </div>
    </form>
  );
}

function BrandingStep({ tenant, onNext, onBack }: { tenant: Tenant; onNext: () => void; onBack: () => void }) {
  const [branding, setBranding] = useState(() => {
    try { return JSON.parse(tenant.branding_json || '{}'); } catch { return {}; }
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await tenantApi.update({ branding_json: JSON.stringify(branding) });
      onNext();
    } finally { setSaving(false); }
  };

  return (
    <div className="stack">
      <div>
        <label>Firm header (printed at top of invoices)</label>
        <input value={branding.header || ''} onChange={(e) => setBranding({ ...branding, header: e.target.value })} placeholder={tenant.name} />
      </div>
      <div>
        <label>Address (invoice footer)</label>
        <textarea value={branding.address || ''} onChange={(e) => setBranding({ ...branding, address: e.target.value })} rows={3} placeholder="1st Floor, Innes Chambers, 32 Protea Road, Sandton 2196" />
      </div>
      <div>
        <label>Contact line (phone / email on invoice)</label>
        <input value={branding.contact || ''} onChange={(e) => setBranding({ ...branding, contact: e.target.value })} placeholder="+27 11 555 0100 · chambers@example.co.za" />
      </div>
      <div>
        <label>Banking details for invoices (optional)</label>
        <textarea value={branding.banking || ''} onChange={(e) => setBranding({ ...branding, banking: e.target.value })} rows={3} placeholder={'Bank: ABSA\nAcc: 4012 345 678\nBranch: 632005'} />
      </div>
      <p className="muted" style={{ fontSize: 12 }}>Logo upload lands in Phase 5. You can paste markdown-ish branding text here for now; it prints on invoices verbatim.</p>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <button onClick={onBack}>← Back</button>
        <button className="primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Continue →'}</button>
      </div>
    </div>
  );
}

function TeamStep({ onNext, onBack }: { onNext: () => void; onBack: () => void }) {
  const [invites, setInvites] = useState<{ email: string; role: string }[]>([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('advocate');
  const [sending, setSending] = useState(false);

  const addInvite = async () => {
    if (!email) return;
    setSending(true);
    try {
      await memberApi.invite({ email, role });
      setInvites([...invites, { email, role }]);
      setEmail('');
    } catch (err) {
      alert('Invite failed: ' + (err as Error).message);
    } finally { setSending(false); }
  };

  return (
    <div className="stack">
      <p className="muted">Invite colleagues by email. They'll receive a link to join. Skip if you're a solo advocate.</p>
      <div className="row">
        <input className="grow" type="email" placeholder="colleague@chambers.co.za" value={email} onChange={(e) => setEmail(e.target.value)} />
        <Select
          value={role}
          onChange={setRole}
          style={{ width: 180 }}
          options={[
            { value: 'advocate',   label: 'Advocate' },
            { value: 'pupil',      label: 'Pupil' },
            { value: 'clerk',      label: 'Clerk' },
            { value: 'accountant', label: 'Accountant' },
            { value: 'admin',      label: 'Admin' },
            { value: 'viewer',     label: 'Viewer' },
          ]}
        />
        <button onClick={addInvite} disabled={sending || !email}>{sending ? '…' : '+ Invite'}</button>
      </div>
      {invites.length > 0 && (
        <div style={{ border: '1px solid var(--color-border)', borderRadius: 8 }}>
          <table>
            <thead><tr><th>Email</th><th>Role</th><th></th></tr></thead>
            <tbody>
              {invites.map((i, idx) => (
                <tr key={idx}><td>{i.email}</td><td>{humaniseRole(i.role)}</td><td><span className="pill success">Invited</span></td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <button onClick={onBack}>← Back</button>
        <button className="primary" onClick={onNext}>Continue →</button>
      </div>
    </div>
  );
}

function IntegrationsStep({ onNext, onBack }: { onNext: () => void; onBack: () => void }) {
  const [smtpOk, setSmtpOk] = useState(false);
  const [aiOk, setAiOk] = useState(false);

  useEffect(() => {
    integrationApi.list().then((r) => {
      if (r.integrations.some((x) => x.kind === 'smtp')) setSmtpOk(true);
    }).catch(() => null);
    aiApi.settings().then((r) => setAiOk(r.configured)).catch(() => null);
  }, []);

  return (
    <div className="stack">
      <p className="muted">You can configure these now or later from Settings.</p>
      <div className="card" style={{ background: 'var(--color-surface-2)' }}>
        <div className="row">
          <div className="grow">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon name="mail" size={18} />
              <b>Email (SMTP)</b>
              {smtpOk && <span className="pill success">Configured</span>}
            </div>
            <p className="muted" style={{ marginTop: 6, fontSize: 13 }}>Used to email invoices and notifications from your chambers mailbox.</p>
          </div>
          <a href="#/settings/integrations/smtp"><button>{smtpOk ? 'Edit' : 'Configure'}</button></a>
        </div>
      </div>
      <div className="card" style={{ background: 'var(--color-surface-2)' }}>
        <div className="row">
          <div className="grow">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon name="sparkle" size={18} />
              <b>AI provider</b>
              {aiOk && <span className="pill success">Configured</span>}
            </div>
            <p className="muted" style={{ marginTop: 6, fontSize: 13 }}>Bring your own Anthropic / OpenAI / Google key for AI chat, document extraction, and drafting.</p>
          </div>
          <a href="#/settings/integrations/ai"><button>{aiOk ? 'Edit' : 'Configure'}</button></a>
        </div>
      </div>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <button onClick={onBack}>← Back</button>
        <button className="primary" onClick={onNext}>Continue →</button>
      </div>
    </div>
  );
}

function FirstMatterStep({ onNext, onBack }: { onNext: () => void; onBack: () => void }) {
  const nav = useNavigate();
  return (
    <div className="stack">
      <p className="muted">Create your first matter to try the app, or finish onboarding and come back later.</p>
      <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
        <div className="card" style={{ flex: 1, minWidth: 260, background: 'var(--color-surface-2)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="briefcase" size={18} /><b>Create a matter</b>
          </div>
          <p className="muted" style={{ fontSize: 13, marginTop: 6 }}>Jump straight to the New Matter form. You can finish onboarding from the dashboard afterwards.</p>
          <button className="primary" onClick={() => nav('/matters/new')} style={{ marginTop: 10 }}>New matter →</button>
        </div>
        <div className="card" style={{ flex: 1, minWidth: 260, background: 'var(--color-surface-2)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="grid" size={18} /><b>Finish and go to dashboard</b>
          </div>
          <p className="muted" style={{ fontSize: 13, marginTop: 6 }}>Skip the first-matter step and land on the dashboard. You can always create matters from there.</p>
          <button onClick={onNext} style={{ marginTop: 10 }}>Finish onboarding →</button>
        </div>
      </div>
      <div className="row" style={{ justifyContent: 'space-between', marginTop: 8 }}>
        <button onClick={onBack}>← Back</button>
      </div>
    </div>
  );
}
